"use client";

import { IdCard } from "lucide-react";

import { cn } from "@/lib/utils";
import type { DriverDisplay } from "@/lib/fleet/driver-ref";

interface DriverLabelProps {
  display: DriverDisplay | null;
  /** Rendered when there is no driver at all; nothing by default. */
  fallback?: React.ReactNode;
  className?: string;
}

/**
 * The three shapes a driver field can take. A `c:` code has no record behind it, so the icon is
 * what tells the reader this is a raw device identifier rather than somebody's name.
 */
export function DriverLabel({ display, fallback = null, className }: DriverLabelProps) {
  if (!display) return <>{fallback}</>;

  if (display.kind === "code") {
    return (
      <span className={cn("inline-flex min-w-0 items-center gap-1", className)}>
        <IdCard className="h-3 w-3 shrink-0" aria-hidden />
        <span className="truncate font-mono">{display.text}</span>
      </span>
    );
  }

  if (display.kind === "unknown") {
    return (
      <span className={cn("truncate italic text-muted-foreground/70", className)}>
        {display.text}
      </span>
    );
  }

  return <span className={cn("truncate", className)}>{display.text}</span>;
}

interface DriverLabelListProps {
  displays: DriverDisplay[];
  /** Rendered when the list is empty. */
  fallback?: React.ReactNode;
  className?: string;
}

/** A route can report several drivers; each keeps its own name / code / muted-id treatment. */
export function DriverLabelList({ displays, fallback = null, className }: DriverLabelListProps) {
  if (displays.length === 0) return <>{fallback}</>;

  return (
    <span className={cn("inline-flex flex-wrap items-center gap-x-1", className)}>
      {displays.map((display, index) => (
        <span key={`${display.kind}:${display.text}`} className="inline-flex items-center">
          <DriverLabel display={display} />
          {index < displays.length - 1 && <span>,</span>}
        </span>
      ))}
    </span>
  );
}
